/**
 * The Copy button over every fenced block.
 *
 * The build writes the button (`plugins/markdown.ts`), because the prose is
 * static HTML; this wires it, once per chapter, by delegation — so a button
 * needs no handler of its own and the chapter's HTML stays a string. It reads
 * « Copied ✓ » for 1.6 s, then goes back.
 */
import { useEffect } from 'react';
import type React from 'react';

const SHOWN_FOR = 1600;

export function useCopyButtons(prose: React.RefObject<HTMLElement | null>, html: string): void {
  useEffect(() => {
    const root = prose.current;
    if (!root) return;
    const timers = new Map<HTMLButtonElement, number>();

    const onClick = (event: MouseEvent): void => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button.al-copy');
      if (!button || !root.contains(button)) return;
      const code = button.closest('.al-codeblock')?.querySelector('pre code');
      if (!code) return;
      const text = code.textContent ?? '';
      try {
        void navigator.clipboard?.writeText(text).catch(() => undefined);
      } catch {
        // no clipboard here: the label still answers, the reader can select
      }
      button.textContent = 'Copied ✓';
      window.clearTimeout(timers.get(button));
      timers.set(
        button,
        window.setTimeout(() => {
          button.textContent = 'Copy';
        }, SHOWN_FOR)
      );
    };

    root.addEventListener('click', onClick);
    return () => {
      root.removeEventListener('click', onClick);
      for (const timer of timers.values()) window.clearTimeout(timer);
    };
  }, [prose, html]);
}
